import { z } from "zod";
import { Id, IsoDateTime } from "./common";
import { LocationConfidence, LocationSource } from "./facility";

/**
 * Asset = the *thing being monitored* (camera unit, generator, vehicle).
 * One or more {@link Device}s feed it telemetry.
 *
 * Phase 2 covers metadata only. State, risk, alerts, and assignments are
 * layered on in Phases 3+. The {@link AssetStatus} field drives soft-delete;
 * physical deletes are reserved for the reset CLI.
 */
export const AssetStatus = z.enum([
  "Active",
  "Disabled",
  "Archived",
  "Retired",
]);
export type AssetStatus = z.infer<typeof AssetStatus>;

export const MaintenanceState = z.enum([
  "Available",
  "InService",
  "NeedsInspection",
  "WorkRequired",
  "Faulted",
  "OutOfService",
]);
export type MaintenanceState = z.infer<typeof MaintenanceState>;

export const AssetCriticality = z.enum(["Low", "Medium", "High", "Critical"]);
export type AssetCriticality = z.infer<typeof AssetCriticality>;

export const AssetType = z.enum([
  "CameraUnit",
  "Generator",
  "Vehicle",
  "Sensor",
  "Pump",
  "Compressor",
  "Hvac",
  "Door",
  "Other",
]);
export type AssetType = z.infer<typeof AssetType>;

const Latitude = z.number().min(-90).max(90).nullable();
const Longitude = z.number().min(-180).max(180).nullable();

export const Asset = z.object({
  assetId: Id,
  tenantId: Id,
  siteId: Id,
  buildingId: Id.nullable(),
  floorId: Id.nullable(),
  assetType: AssetType,
  name: z.string().min(1).max(120),
  normalizedName: z.string().min(1).max(120),
  description: z.string().max(2000).nullable(),
  status: AssetStatus,
  maintenanceState: MaintenanceState,
  criticality: AssetCriticality,
  manufacturer: z.string().max(120).nullable(),
  model: z.string().max(120).nullable(),
  serialNumber: z.string().max(120).nullable(),
  externalLabel: z.string().max(120).nullable(),
  latitude: Latitude,
  longitude: Longitude,
  locationSource: LocationSource,
  locationConfidence: LocationConfidence,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  createdBy: Id.nullable(),
  updatedBy: Id.nullable(),
});
export type Asset = z.infer<typeof Asset>;

export const AssetCreateInput = z.object({
  siteId: Id,
  buildingId: Id.nullable().optional(),
  floorId: Id.nullable().optional(),
  assetType: AssetType,
  name: z.string().min(1).max(120),
  description: z.string().max(2000).nullable().optional(),
  maintenanceState: MaintenanceState.optional(),
  criticality: AssetCriticality.optional(),
  manufacturer: z.string().max(120).nullable().optional(),
  model: z.string().max(120).nullable().optional(),
  serialNumber: z.string().max(120).nullable().optional(),
  externalLabel: z.string().max(120).nullable().optional(),
  latitude: Latitude.optional(),
  longitude: Longitude.optional(),
  locationSource: LocationSource.optional(),
  locationConfidence: LocationConfidence.optional(),
});
export type AssetCreateInput = z.infer<typeof AssetCreateInput>;

export const AssetUpdateInput = z
  .object({
    siteId: Id,
    buildingId: Id.nullable(),
    floorId: Id.nullable(),
    assetType: AssetType,
    name: z.string().min(1).max(120),
    description: z.string().max(2000).nullable(),
    status: AssetStatus,
    maintenanceState: MaintenanceState,
    criticality: AssetCriticality,
    manufacturer: z.string().max(120).nullable(),
    model: z.string().max(120).nullable(),
    serialNumber: z.string().max(120).nullable(),
    externalLabel: z.string().max(120).nullable(),
    latitude: Latitude,
    longitude: Longitude,
    locationSource: LocationSource,
    locationConfidence: LocationConfidence,
  })
  .partial();
export type AssetUpdateInput = z.infer<typeof AssetUpdateInput>;
